import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import CountUp from 'react-countup';
import { ethers } from 'ethers';

// === ANIMATED DOLLAR VALUE ===
export function AnimVal({ value, prefix = '$', className = '', decimals = 2, color }) {
  const num = useMemo(() => {
    try { const n = parseFloat(ethers.utils.formatUnits(value, 18)); return Math.floor(n * 100) / 100; } catch { return 0; }
  }, [value]);
  return (
    <span className={className} style={color ? { color } : {}}>
      {prefix}<CountUp end={num} decimals={decimals} duration={1.5} separator="," preserveValue />
    </span>
  );
}

// === ANIMATED NUMBER ===
export function AnimNum({ value, className = '' }) {
  const num = useMemo(() => {
    try { return parseInt(ethers.BigNumber.from(value).toString()); } catch { return 0; }
  }, [value]);
  return <span className={className}><CountUp end={num} duration={1.2} preserveValue /></span>;
}

// === CONFETTI ===
const COLORS = ['#00F5FF', '#7B61FF', '#FFB800', '#FF6EC7', '#00E676', '#FFC940'];
export function Confetti({ active, duration = 3000 }) {
  const [show, setShow] = useState(false);
  const pieces = useMemo(() => Array.from({ length: 60 }, (_, i) => ({
    left: Math.random() * 100 + '%',
    bg: COLORS[Math.floor(Math.random() * COLORS.length)],
    dur: (1.5 + Math.random() * 2) + 's',
    del: (Math.random() * 0.8) + 's',
    size: (5 + Math.random() * 8) + 'px',
    rot: Math.random() * 360 + 'deg'
  })), []);

  useEffect(() => {
    if (active) { setShow(true); const t = setTimeout(() => setShow(false), duration); return () => clearTimeout(t); }
  }, [active, duration]);

  if (!show) return null;
  return (
    <div className="confetti-wrap">
      {pieces.map((p, i) => <div key={i} className="confetti-piece" style={{ left: p.left, background: p.bg, '--dur': p.dur, '--del': p.del, width: p.size, height: p.size, transform: `rotate(${p.rot})` }} />)}
    </div>
  );
}

// === FLIP CLOCK TIMER ===
export function FlipClock({ targetTime }) {
  const [now, setNow] = useState(Date.now() / 1000);
  useEffect(() => { const t = setInterval(() => setNow(Date.now() / 1000), 1000); return () => clearInterval(t); }, []);

  const diff = Math.max(0, targetTime - now);
  const h = Math.floor(diff / 3600);
  const m = Math.floor((diff % 3600) / 60);
  const s = Math.floor(diff % 60);
  const pad = (n) => String(n).padStart(2, '0');

  if (diff <= 0) return <span style={{ color: 'var(--green)', fontWeight: 700, fontSize: '0.85rem' }}>⚡ Ready to claim!</span>;

  return (
    <div className="flip-clock">
      <div className="flip-unit"><div className="flip-digit">{pad(h)}</div><div className="flip-label">hrs</div></div>
      <span className="flip-sep">:</span>
      <div className="flip-unit"><div className="flip-digit">{pad(m)}</div><div className="flip-label">min</div></div>
      <span className="flip-sep">:</span>
      <div className="flip-unit"><div className="flip-digit">{pad(s)}</div><div className="flip-label">sec</div></div>
    </div>
  );
}

// === ROI TIMER WITH PENDING COUNT ===
export function ROITimer({ lastROITimestamp, timeUnit, pendingPeriods }) {
  const [now, setNow] = useState(Date.now() / 1000);
  useEffect(() => { const t = setInterval(() => setNow(Date.now() / 1000), 1000); return () => clearInterval(t); }, []);

  const elapsed = now - lastROITimestamp;
  const completedPeriods = Math.floor(elapsed / timeUnit);
  const nextPeriodAt = lastROITimestamp + ((completedPeriods + 1) * timeUnit);
  const diff = Math.max(0, nextPeriodAt - now);
  const h = Math.floor(diff / 3600);
  const m = Math.floor((diff % 3600) / 60);
  const s = Math.floor(diff % 60);
  const pad = (n) => String(n).padStart(2, '0');

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
      <div>
        <div style={{ fontSize: '0.55rem', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: 4, textAlign: 'center' }}>Next ROI in</div>
        <div className="flip-clock">
          <div className="flip-unit"><div className="flip-digit">{pad(h)}</div></div>
          <span className="flip-sep">:</span>
          <div className="flip-unit"><div className="flip-digit">{pad(m)}</div></div>
          <span className="flip-sep">:</span>
          <div className="flip-unit"><div className="flip-digit">{pad(s)}</div></div>
        </div>
      </div>
      {completedPeriods > 0 && (
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', bounce: 0.5 }}
          style={{ background: 'rgba(255,71,87,0.12)', border: '1px solid rgba(255,71,87,0.2)', borderRadius: 12, padding: '8px 14px', textAlign: 'center' }}>
          <div style={{ fontFamily: 'var(--font-h)', fontSize: '1.2rem', fontWeight: 800, color: 'var(--gold)' }}>{completedPeriods}</div>
          <div style={{ fontSize: '0.5rem', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '1px' }}>Pending</div>
        </motion.div>
      )}
    </div>
  );
}

// === FUTURE STAKING TIMER (30-cycle with pending count, like ROI timer) ===
export function FutureStakingTimer({ cycleStart, timeUnit, cycleDays = 30, onReady }) {
  const [now, setNow] = useState(Date.now() / 1000);
  useEffect(() => { const t = setInterval(() => setNow(Date.now() / 1000), 1000); return () => clearInterval(t); }, []);

  if (!cycleStart || cycleStart === 0) return null;

  const cycleLength = timeUnit * cycleDays; // total time for 1 cycle
  const elapsed = now - cycleStart;
  const completedCycles = Math.floor(elapsed / cycleLength);
  const nextCycleAt = cycleStart + ((completedCycles + 1) * cycleLength);
  const diff = Math.max(0, nextCycleAt - now);

  const isReady = completedCycles > 0;
  useEffect(() => { if (isReady && onReady) onReady(true); }, [isReady, onReady]);

  const d = Math.floor(diff / 86400);
  const h = Math.floor((diff % 86400) / 3600);
  const m = Math.floor((diff % 3600) / 60);
  const s = Math.floor(diff % 60);
  const pad = (n) => String(n).padStart(2, '0');

  // Progress percentage within current cycle
  const cycleElapsed = elapsed - (completedCycles * cycleLength);
  const progressPct = Math.min((cycleElapsed / cycleLength) * 100, 100);

  return (
    <div className="fs-timer-wrap">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: '0.55rem', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: 6, textAlign: 'center' }}>
            {isReady ? 'Claim Available!' : 'Next Payout In'}
          </div>
          {!isReady ? (
            <div className="flip-clock">
              {d > 0 && <><div className="flip-unit"><div className="flip-digit">{pad(d)}</div><div className="flip-label">days</div></div><span className="flip-sep">:</span></>}
              <div className="flip-unit"><div className="flip-digit">{pad(h)}</div><div className="flip-label">hrs</div></div>
              <span className="flip-sep">:</span>
              <div className="flip-unit"><div className="flip-digit">{pad(m)}</div><div className="flip-label">min</div></div>
              <span className="flip-sep">:</span>
              <div className="flip-unit"><div className="flip-digit">{pad(s)}</div><div className="flip-label">sec</div></div>
            </div>
          ) : (
            <div style={{ color: 'var(--green)', fontWeight: 700, fontSize: '0.9rem', textAlign: 'center' }}>⚡ Ready to claim!</div>
          )}
        </div>
        {completedCycles > 0 && (
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', bounce: 0.5 }}
            style={{ background: 'rgba(0,230,118,0.12)', border: '1px solid rgba(0,230,118,0.25)', borderRadius: 12, padding: '8px 14px', textAlign: 'center' }}>
            <div style={{ fontFamily: 'var(--font-h)', fontSize: '1.2rem', fontWeight: 800, color: 'var(--green)' }}>{completedCycles}</div>
            <div style={{ fontSize: '0.5rem', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '1px' }}>Pending</div>
          </motion.div>
        )}
      </div>
      {/* Progress bar */}
      {!isReady && (
        <div style={{ marginTop: 12, height: 5, borderRadius: 3, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
          <motion.div initial={{ width: 0 }} animate={{ width: `${progressPct}%` }} transition={{ duration: 1 }}
            style={{ height: '100%', borderRadius: 3, background: 'linear-gradient(90deg, var(--purple), var(--cyan))' }} />
        </div>
      )}
    </div>
  );
}

// === FUTURE STAKING GAUGE (speedometer) ===
export function FutureStakingGauge({ earned, cap }) {
  const e = parseFloat(ethers.utils.formatUnits(earned, 18));
  const c = parseFloat(ethers.utils.formatUnits(cap, 18));
  const pct = c > 0 ? Math.min((e / c) * 100, 100) : 0;
  const arcLen = 251, offset = arcLen - (arcLen * pct / 100);
  const color = pct < 50 ? '#7B61FF' : pct < 80 ? '#FFB800' : '#FF4757';

  return (
    <div className="gauge-wrap" style={{ transform: 'scale(0.85)' }}>
      <svg className="gauge-svg" viewBox="0 0 180 110">
        <path d="M 10 100 A 80 80 0 0 1 170 100" className="gauge-bg" />
        <motion.path d="M 10 100 A 80 80 0 0 1 170 100" className="gauge-fill"
          style={{ '--arc': offset, stroke: color, filter: `drop-shadow(0 0 8px ${color})` }}
          initial={{ strokeDashoffset: 251 }} animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1.8, ease: 'easeOut', delay: 0.3 }} />
      </svg>
      <div className="gauge-center">
        <div className="gauge-pct" style={{ fontSize: '1rem' }}>{pct.toFixed(1)}%</div>
        <div className="gauge-sub" style={{ fontSize: '0.42rem' }}>2x Cap Used</div>
      </div>
    </div>
  );
}

// === PROGRESS RING (for avatar) ===
export function ProgressRing({ percent = 0, size = 64, strokeWidth = 3, color = 'var(--cyan)', children }) {
  const r = (size - strokeWidth * 2) / 2;
  const circumference = 2 * Math.PI * r;
  const offset = circumference - (percent / 100) * circumference;

  return (
    <div className="progress-ring" style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} className="progress-ring-bg" strokeWidth={strokeWidth} />
        <circle cx={size / 2} cy={size / 2} r={r} className="progress-ring-fill"
          stroke={color} strokeWidth={strokeWidth}
          strokeDasharray={circumference} strokeDashoffset={offset} />
      </svg>
      <div className="progress-ring-content">{children}</div>
    </div>
  );
}

// === FLOATING CLAIM BADGE ===
export function FloatingClaim({ amount, onClick }) {
  if (!amount || amount <= 0) return null;
  return (
    <motion.div className="float-claim" initial={{ y: 100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 100, opacity: 0 }}>
      <div className="float-claim-btn" onClick={onClick}>
        <span>📈</span>
        <span>Claim ${amount.toFixed(2)}</span>
      </div>
      <div className="float-claim-pulse" />
    </motion.div>
  );
}

// === SKELETON LOADER ===
export function SkeletonCards({ count = 4 }) {
  return (
    <div style={{ padding: 16 }}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="skeleton skeleton-box" style={{ marginBottom: 12, height: i === 0 ? 160 : 80 }} />
      ))}
    </div>
  );
}

// === MOTION PAGE WRAPPER ===
export function PageWrap({ children }) {
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3, ease: 'easeOut' }} className="page">
      {children}
    </motion.div>
  );
}

// === MOTION CARD ===
export function MCard({ children, delay = 0, className = 'card', style, onClick }) {
  return (
    <motion.div className={`${className} spotlight-card`} style={style} onClick={onClick}
      initial={{ opacity: 0, y: 30, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay, duration: 0.4, type: 'spring', stiffness: 200, damping: 25 }}
      whileHover={{ y: -3, transition: { duration: 0.2 } }}
      whileTap={onClick ? { scale: 0.98 } : {}}>
      {children}
    </motion.div>
  );
}
